import { useState, useRef, useEffect } from 'react';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import styles from './RegisterCom.module.css';
import { useMutation } from "@tanstack/react-query";
import axios from 'axios';
import {
    aadhar_payment_fraud, upi_fraud, business_email_compromise, credit_fraud,
    demat_fraud, ewallet_fraud, fraud_call, internet_banking_fraud, payment_network_fraud,
} from '../../constants/apiEndpoints'
import { Dialog } from 'primereact/dialog';
import html2canvas from 'html2canvas';
import { Calendar } from 'primereact/calendar';
import { Toast } from 'primereact/toast';

const RegisterComplaint = () => {

    const toast = useRef(null);

    const showSuccess = () => {
        toast.current.show({ severity: 'success', summary: 'Success', detail: 'Incident details submitted', life: 3000 });
    }

    const showError = () => {
        toast.current.show({ severity: 'error', summary: 'Error', detail: 'Error submitting details', life: 3000 });
    }

    const [formValues, setFormValues] = useState({
        acknowledgementNo: '',
        requestor: '',
        payerBank: '',
        payerBankCode: '',
        modeOfPayment: '',
        payerMobileNumber: '',
        payerAccountNumber: '',
        state: '',
        district: '',
        subCategory: '',
        wallet: '',
        transactionType: '',
        fraudType: '',
    });

    const [incidents, setIncidents] = useState([
        {
            amount: '',
            rrn: '',
            date: '',
            time: new Date(),
            disamount: '',
            layer: '',
            first6Digit: '',
            last4Digit: '',
            cardLength: '',
        },
    ]);

    const incidentRefs = useRef([]);
    const [justAddedIncident, setJustAddedIncident] = useState(null);

    const [errors, setErrors] = useState({});
    const [incidentErrors, setIncidentErrors] = useState([]);

    const [displayDialog, setDisplayDialog] = useState(false);
    const [incidentResponse, setIncidentResponse] = useState({});
    const captureRef = useRef(null);

    const fraudTypes = [
        { label: 'Aadhar Payment Fraud', value: 'aadhar_payment_fraud' },
        { label: 'UPI Fraud', value: 'upi_fraud' },
        { label: 'Business Email Compromise', value: 'business_email_compromise' },
        { label: 'Credit fraud', value: 'credit_fraud' },
        { label: 'Demat Fraud', value: 'demat_fraud' },
        { label: 'Ewallet Fraud', value: 'ewallet_fraud' },
        { label: 'Fraud Call', value: 'fraud_call' },
        { label: 'Internet Banking Fraud', value: 'internet_banking_fraud' },
        { label: 'Payment Network Fraud', value: 'payment_network_fraud' },
    ];

    const apiEndpoints = {
        aadhar_payment_fraud: `${aadhar_payment_fraud}`,
        upi_fraud: `${upi_fraud}`,
        business_email_compromise: `${business_email_compromise}`,
        credit_fraud: `${credit_fraud}`,
        demat_fraud: `${demat_fraud}`,
        ewallet_fraud: `${ewallet_fraud}`,
        fraud_call: `${fraud_call}`,
        internet_banking_fraud: `${internet_banking_fraud}`,
        payment_network_fraud: `${payment_network_fraud}`
    };

    const generateAcknowledgementNo = () => {
        const timestamp = new Date().getTime();
        const random = Math.floor(Math.random() * 10000);
        return `ACK-${timestamp}-${random}`;
    };

    useEffect(() => {
        setFormValues((prev) => ({
            ...prev,
            acknowledgementNo: generateAcknowledgementNo(),
        }));
    }, []);

    const handleChange = (e) => {
        setFormValues((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    };

    const handleDropdownChange = (e) => {
        setFormValues((prev) => ({ ...prev, fraudType: e.value }));
    };

    const handleChangeInc = (index, e) => {
        const { name, value } = e.target;
        const newIncidents = [...incidents];
        newIncidents[index][name] = value;
        setIncidents(newIncidents);
    };

    const addIncident = () => {
        setIncidents([
            ...incidents,
            {
                amount: '',
                rrn: '',
                date: '',
                time: new Date(),
                disamount: '',
                layer: '',
                first6Digit: '',
                last4Digit: '',
                cardLength: '',
            },
        ]);
        setIncidentErrors((prev) => [...prev, {}]);
        setJustAddedIncident(incidents.length);
    };

    const removeIncident = (index) => {
        const newIncidents = [...incidents];
        newIncidents.splice(index, 1);
        setIncidents(newIncidents);
    };

    useEffect(() => {
        if (
            justAddedIncident !== null &&
            incidentRefs.current[justAddedIncident]
        ) {
            incidentRefs.current[justAddedIncident].scrollIntoView({
                behavior: 'smooth',
                block: 'start',
            });
            setJustAddedIncident(null);
        }
    }, [incidents, justAddedIncident]);

    const isCreditFraudSelected = formValues.fraudType === 'credit_fraud';
    const isUpiFraudSelected = formValues.fraudType === 'upi_fraud' || formValues.fraudType === 'aadhar_payment_fraud';

    // Regex for date and time validation
    const dateRegex = /^\d{4}-\d{2}-\d{2}$/;  // YY-MM-DD
    const timeRegex = /^\d{2}:\d{2}:\d{2}$/;  // HH:MM:SS

    const validate = () => {
        const newErrors = {};
        const newIncidentErrors = [];

        if (!formValues.requestor.trim()) {
            newErrors.requestor = 'Requestor is required';
        }

        if (!formValues.payerBank.trim()) {
            newErrors.payerBank = 'Payer Bank is required';
        }

        if (!formValues.payerBankCode.trim()) {
            newErrors.payerBankCode = 'Payer Bank Code is required';
        }

        if (!formValues.modeOfPayment.trim()) {
            newErrors.modeOfPayment = 'Mode of Payment is required';
        }

        if (!formValues.payerMobileNumber.trim()) {
            newErrors.payerMobileNumber = 'Payer Mobile Number is required';
        } else if (!/^\d{10}$/.test(formValues.payerMobileNumber.trim())) {
            newErrors.payerMobileNumber = 'Enter a valid 10-digit mobile number';
        }

        if (!formValues.payerAccountNumber.trim()) {
            newErrors.payerAccountNumber = 'Payer Account Number is required';
        }

        if (!formValues.state.trim()) {
            newErrors.state = 'State is required';
        }

        if (!formValues.district.trim()) {
            newErrors.district = 'District is required';
        }

        if (!formValues.subCategory.trim()) {
            newErrors.subCategory = 'Sub Category is required';
        }

        // if (isUpiFraudSelected) {
        //     if (!formValues.wallet.trim()) {
        //         newErrors.wallet = 'Wallet is required';
        //     }
        // }

        if (!formValues.transactionType.trim()) {
            newErrors.transactionType = 'Transaction Type is required';
        }

        if (!formValues.fraudType) {
            newErrors.fraudType = 'Select a Fraud Type';
        }

        incidents.forEach((incident, idx) => {
            const ie = {};

            if (!incident.amount.trim()) {
                ie.amount = 'Amount is required';
            }

            if (!incident.rrn.trim()) {
                ie.rrn = 'RRN is required';
            }

            const date = new Date(incident.date);
            const formattedDate = `${date.getFullYear()}-${('0' + (date.getMonth() + 1)).slice(-2)}-${('0' + date.getDate()).slice(-2)}`;

            if (!incident.date) {
                ie.date = 'Transaction Date is required';
            } else if (!dateRegex.test(formattedDate)) {
                ie.date = 'Date format must be YY-MM-DD';
            }

            const date1 = new Date(incident.time);
            const timeOnly = date1.toTimeString().split(' ')[0];

            if (!incident.time) {
                ie.time = 'Transaction Time is required';
            } else if (!timeRegex.test(timeOnly)) {
                ie.time = 'Time format must be HH:MM:SS';
            }

            if (!incident.disamount.trim()) {
                ie.disamount = 'Disputed Amount is required';
            }

            if (!incident.layer.trim()) {
                ie.layer = 'Layer is required';
            }

            if (isCreditFraudSelected) {
                if (!incident.first6Digit.trim()) {
                    ie.first6Digit = 'First 6 digits are required';
                }
                if (!incident.last4Digit.trim()) {
                    ie.last4Digit = 'Last 4 digits are required';
                }
                if (!incident.cardLength.trim()) {
                    ie.cardLength = 'Card length is required';
                }
            }

            newIncidentErrors[idx] = ie;
        });

        setErrors(newErrors);
        setIncidentErrors(newIncidentErrors);

        return (
            Object.keys(newErrors).length === 0 &&
            newIncidentErrors.every((e) => Object.keys(e).length === 0)
        );
    };

    const handleFocus = (fieldName) => {
        if (errors[fieldName]) {
            setErrors((prev) => {
                const newErrors = { ...prev };
                delete newErrors[fieldName];
                return newErrors;
            });
        }
    };

    const handleFocusInc = (index, fieldName) => {
        if (incidentErrors[index] && incidentErrors[index][fieldName]) {
            setIncidentErrors((prev) => {
                const newIncidentErrors = [...prev];
                if (newIncidentErrors[index]) {
                    delete newIncidentErrors[index][fieldName];
                }
                return newIncidentErrors;
            });
        }
    };

    const incidentData = useMutation({
        mutationFn: (incidentUpdate) => {
            const endpoint = apiEndpoints[formValues.fraudType];
            if (!endpoint) {
                throw new Error('Invalid fraud type selected');
            }
            return axios.post(endpoint, incidentUpdate);
        },
    });

    const formatDate = (dates) => {
        const date = new Date(dates);
        return `${date.getFullYear()}-${('0' + (date.getMonth() + 1)).slice(-2)}-${('0' + date.getDate()).slice(-2)}`;
    }

    const formatTime = (times) => {
        const time = new Date(times);
        return time.toTimeString().split(' ')[0];
    }

    const resetPage = () => {

        setDisplayDialog(false);
        setFormValues({
            acknowledgementNo: generateAcknowledgementNo(),
            requestor: '',
            payerBank: '',
            payerBankCode: '',
            modeOfPayment: '',
            payerMobileNumber: '',
            payerAccountNumber: '',
            state: '',
            district: '',
            subCategory: '',
            wallet: '',
            transactionType: '',
            fraudType: '',
        })

        setIncidents([
            {
                amount: '',
                rrn: '',
                date: '',
                time: '',
                disamount: '',
                layer: '',
                first6Digit: '',
                last4Digit: '',
                cardLength: '',
            },
        ]);
    }

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (validate()) {

            try {
                const formattedIncidents = incidents.map(incident => ({
                    amount: parseFloat(incident.amount),
                    RRN: parseInt(incident.rrn),
                    transaction_date: formatDate(incident.date),
                    transaction_time: formatTime(incident.time),
                    disputed_amount: parseFloat(incident.disamount),
                    layer: parseInt(incident.layer),
                    ...(isCreditFraudSelected && {
                        first6digit: incident.first6Digit,
                        last4digit: incident.last4Digit,
                        cardlength: incident.cardLength,
                    })
                }));

                const incidentUpdate = {
                    acknowledgement_no: formValues.acknowledgementNo,
                    sub_category: formValues.subCategory,
                    requestor: formValues.requestor,
                    payer_bank: formValues.payerBank,
                    payer_bank_code: parseInt(formValues.payerBankCode),
                    mode_of_payment: formValues.modeOfPayment,
                    payer_mobile_number: formValues.payerMobileNumber,
                    payer_account_number: formValues.payerAccountNumber,
                    state: formValues.state,
                    district: formValues.district,
                    transaction_type: formValues.transactionType,
                    ...(isUpiFraudSelected && formValues.wallet !== '' && {
                        wallet: formValues.wallet,
                    }),
                    incidents: formattedIncidents
                };
                console.log(incidentUpdate)
                const incidentRes = await incidentData.mutateAsync(incidentUpdate);
                showSuccess()
                setIncidentResponse(incidentRes.data.data);
                setDisplayDialog(true);

            } catch (error) {
                console.log(error)
                showError();
                // setIncidentResponse(error.response.data.data);
                // setDisplayDialog(true);
                // setFormValues({
                //     acknowledgementNo: '',
                //     requestor: '',
                //     payerBank: '',
                //     payerBankCode: '',
                //     modeOfPayment: '',
                //     payerMobileNumber: '',
                //     payerAccountNumber: '',
                //     state: '',
                //     district: '',
                //     subCategory: '',
                //     wallet: '',
                //     transactionType: '',
                //     fraudType: '',
                // })
            }
        }
    };

    const takeScreenshot = async () => {
        const element = captureRef.current;
        const canvas = await html2canvas(element);
        const image = canvas.toDataURL('image/png');
        const link = document.createElement('a');
        link.href = image;
        link.download = 'incident.png';
        link.click();
        resetPage();
    };

    const dialogFooter = (
        <div>
            <Button label="Ok" className="p-button-primary" onClick={resetPage} />
            <Button label="Capture Screenshot" className="p-button-secondary" onClick={takeScreenshot} />
        </div>
    );

    return (
        <>
            <Toast ref={toast} />
            <div className={`${styles.incontainer}`}>
                <div className={styles.container}>
                    <h2 className={styles.registerComplaintTitle}>Register Complaint</h2>

                    <form onSubmit={handleSubmit} noValidate>
                        <div className={styles.formRow}>
                            <div className={styles.formField}>
                                <label htmlFor="acknowledgementNo" className={styles.label}>
                                    Acknowledgement No
                                </label>
                                <InputText
                                    id="acknowledgementNo"
                                    name="acknowledgementNo"
                                    placeholder="Auto-generated"
                                    value={formValues.acknowledgementNo}
                                    readOnly
                                    className={styles.inputField}
                                    onFocus={() => handleFocus('acknowledgementNo')}
                                    aria-invalid={!!errors.acknowledgementNo}
                                />
                                {errors.acknowledgementNo && (
                                    <small className={styles.errorMsg}>{errors.acknowledgementNo}</small>
                                )}
                            </div>
                            <div className={styles.formField}>
                                <label htmlFor="requestor" className={styles.label}>
                                    Requestor
                                </label>
                                <InputText
                                    id="requestor"
                                    name="requestor"
                                    placeholder="Enter requestor name"
                                    value={formValues.requestor}
                                    onChange={handleChange}
                                    className={styles.inputField}
                                    onFocus={() => handleFocus('requestor')}
                                    aria-invalid={!!errors.requestor}
                                />
                                {errors.requestor && (
                                    <small className={styles.errorMsg}>{errors.requestor}</small>
                                )}
                            </div>
                        </div>

                        <div className={styles.formRow}>
                            <div className={styles.formField}>
                                <label htmlFor="payerBank" className={styles.label}>
                                    Payer Bank
                                </label>
                                <InputText
                                    id="payerBank"
                                    name="payerBank"
                                    placeholder="Enter payer bank"
                                    value={formValues.payerBank}
                                    onChange={handleChange}
                                    className={styles.inputField}
                                    onFocus={() => handleFocus('payerBank')}
                                    aria-invalid={!!errors.payerBank}
                                />
                                {errors.payerBank && (
                                    <small className={styles.errorMsg}>{errors.payerBank}</small>
                                )}
                            </div>
                            <div className={styles.formField}>
                                <label htmlFor="payerBankCode" className={styles.label}>
                                    Payer Bank Code
                                </label>
                                <InputText
                                    id="payerBankCode"
                                    name="payerBankCode"
                                    placeholder="Enter payer bank code"
                                    value={formValues.payerBankCode}
                                    onChange={handleChange}
                                    onFocus={() => handleFocus('payerBankCode')}
                                    className={styles.inputField}
                                    aria-invalid={!!errors.payerBankCode}
                                />
                                {errors.payerBankCode && (
                                    <small className={styles.errorMsg}>{errors.payerBankCode}</small>
                                )}
                            </div>
                        </div>

                        <div className={styles.formRow}>
                            <div className={styles.formField}>
                                <label htmlFor="modeOfPayment" className={styles.label}>
                                    Mode of Payment
                                </label>
                                <InputText
                                    id="modeOfPayment"
                                    name="modeOfPayment"
                                    placeholder="Enter mode of payment"
                                    value={formValues.modeOfPayment}
                                    onChange={handleChange}
                                    onFocus={() => handleFocus('modeOfPayment')}
                                    className={styles.inputField}
                                    aria-invalid={!!errors.modeOfPayment}
                                />
                                {errors.modeOfPayment && (
                                    <small className={styles.errorMsg}>{errors.modeOfPayment}</small>
                                )}
                            </div>
                            <div className={styles.formField}>
                                <label htmlFor="payerMobileNumber" className={styles.label}>
                                    Payer Mobile Number
                                </label>
                                <InputText
                                    id="payerMobileNumber"
                                    name="payerMobileNumber"
                                    placeholder="Enter payer mobile number"
                                    value={formValues.payerMobileNumber}
                                    onChange={handleChange}
                                    onFocus={() => handleFocus('payerMobileNumber')}
                                    className={styles.inputField}
                                    aria-invalid={!!errors.payerMobileNumber}
                                />
                                {errors.payerMobileNumber && (
                                    <small className={styles.errorMsg}>{errors.payerMobileNumber}</small>
                                )}
                            </div>
                        </div>

                        <div className={styles.formRow}>
                            <div className={styles.formField}>
                                <label htmlFor="payerAccountNumber" className={styles.label}>
                                    Payer Account Number
                                </label>
                                <InputText
                                    id="payerAccountNumber"
                                    name="payerAccountNumber"
                                    placeholder="Enter payer account number"
                                    value={formValues.payerAccountNumber}
                                    onChange={handleChange}
                                    onFocus={() => handleFocus('payerAccountNumber')}
                                    className={styles.inputField}
                                    aria-invalid={!!errors.payerAccountNumber}
                                />
                                {errors.payerAccountNumber && (
                                    <small className={styles.errorMsg}>{errors.payerAccountNumber}</small>
                                )}
                            </div>
                            <div className={styles.formField}>
                                <label htmlFor="State" className={styles.label}>
                                    State
                                </label>
                                <InputText
                                    id="state"
                                    name="state"
                                    placeholder="Enter state"
                                    value={formValues.state}
                                    onChange={handleChange}
                                    onFocus={() => handleFocus('state')}
                                    className={styles.inputField}
                                    aria-invalid={!!errors.state}
                                />
                                {errors.state && (
                                    <small className={styles.errorMsg}>{errors.state}</small>
                                )}
                            </div>
                        </div>

                        <div className={styles.formRow}>
                            <div className={styles.formField}>
                                <label htmlFor="district" className={styles.label}>
                                    District
                                </label>
                                <InputText
                                    id="district"
                                    name="district"
                                    placeholder="Enter district"
                                    value={formValues.district}
                                    onChange={handleChange}
                                    onFocus={() => handleFocus('district')}
                                    className={styles.inputField}
                                    aria-invalid={!!errors.district}
                                />
                                {errors.district && (
                                    <small className={styles.errorMsg}>{errors.district}</small>
                                )}
                            </div>
                            <div className={styles.formField}>
                                <label htmlFor="subCategory" className={styles.label}>
                                    Sub Category
                                </label>
                                <InputText
                                    id="subCategory"
                                    name="subCategory"
                                    placeholder="Enter sub category"
                                    value={formValues.subCategory}
                                    onChange={handleChange}
                                    onFocus={() => handleFocus('subCategory')}
                                    className={styles.inputField}
                                    aria-invalid={!!errors.subCategory}
                                />
                                {errors.subCategory && (
                                    <small className={styles.errorMsg}>{errors.subCategory}</small>
                                )}
                            </div>
                        </div>

                        <div className={styles.formRow}>
                            <div className={`${styles.formField} ${styles.customMultiSelectContainer}`}>
                                <label htmlFor="fraudType" className={styles.label}>
                                    Select Fraud Type
                                </label>
                                <Dropdown
                                    id="fraudType"
                                    name="fraudType"
                                    value={formValues.fraudType}
                                    onChange={handleDropdownChange}
                                    options={fraudTypes}
                                    placeholder="Select Fraud Type"
                                    onFocus={() => handleFocus('fraudType')}
                                    aria-invalid={!!errors.fraudType}
                                />
                                {errors.fraudType && (
                                    <small className={styles.errorMsg}>{errors.fraudType}</small>
                                )}
                            </div>
                        </div>

                        <div className={styles.formRow}>
                            <div className={styles.formField}>
                                <label htmlFor="transactionType" className={styles.label}>
                                    Transaction Type
                                </label>
                                <InputText
                                    id="transactionType"
                                    name="transactionType"
                                    placeholder="Enter transaction type"
                                    value={formValues.transactionType}
                                    onChange={handleChange}
                                    onFocus={() => handleFocus('transactionType')}
                                    className={styles.inputField}
                                    aria-invalid={!!errors.transactionType}
                                />
                                {errors.transactionType && (
                                    <small className={styles.errorMsg}>{errors.transactionType}</small>
                                )}
                            </div>
                            {isUpiFraudSelected &&
                                <div className={styles.formField}>
                                    <label htmlFor="wallet" className={styles.label}>
                                        Wallet
                                    </label>
                                    <InputText
                                        id="wallet"
                                        name="wallet"
                                        placeholder="Enter wallet"
                                        value={formValues.wallet}
                                        onChange={handleChange}
                                        onFocus={() => handleFocus('wallet')}
                                        className={styles.inputField}
                                        aria-invalid={!!errors.wallet}
                                    />
                                    {errors.wallet && (
                                        <small className={styles.errorMsg}>{errors.wallet}</small>
                                    )}
                                </div>
                            }
                        </div>

                        {incidents.map((incident, index) => (
                            <div
                                key={index}
                                ref={el => incidentRefs.current[index] = el}
                                style={{ marginBottom: '1.5rem', border: '1px solid #ddd', padding: '1rem' }}
                            >
                                <div className="flex align-items-center">
                                    <h3 className={styles.registerComplaintTitle}>Incident {index + 1}</h3>
                                    {index === incidents.length - 1 && (
                                        <i
                                            className="pi pi-plus-circle ml-3"
                                            style={{ fontSize: '1.2rem', cursor: 'pointer' }}
                                            onClick={addIncident}
                                            aria-label="Add new incident"
                                        />
                                    )}

                                    {index > 0 && (
                                        <i
                                            className="pi pi-minus-circle ml-2"
                                            style={{ fontSize: '1.2rem', cursor: 'pointer', color: 'red' }}
                                            onClick={() => removeIncident(index)}
                                        ></i>
                                    )}
                                </div>

                                <div className={styles.formRow}>
                                    <div className={styles.formField}>
                                        <label htmlFor={`amount-${index}`} className={styles.label}>
                                            Amount
                                        </label>
                                        <InputText
                                            id={`amount-${index}`}
                                            name="amount"
                                            placeholder="Enter amount"
                                            value={incident.amount}
                                            onChange={(e) => handleChangeInc(index, e)}
                                            onFocus={() => handleFocusInc(index, 'amount')}
                                            className={styles.inputField}
                                            aria-invalid={!!(incidentErrors[index] && incidentErrors[index].amount)}
                                        />
                                        {incidentErrors[index] && incidentErrors[index].amount && (
                                            <small className={styles.errorMsg}>{incidentErrors[index].amount}</small>
                                        )}
                                    </div>
                                    <div className={styles.formField}>
                                        <label htmlFor={`rrn-${index}`} className={styles.label}>
                                            RRN
                                        </label>
                                        <InputText
                                            id={`rrn-${index}`}
                                            name="rrn"
                                            placeholder="Enter RRN"
                                            value={incident.rrn}
                                            onChange={(e) => handleChangeInc(index, e)}
                                            onFocus={() => handleFocusInc(index, 'rrn')}
                                            className={styles.inputField}
                                            aria-invalid={!!(incidentErrors[index] && incidentErrors[index].rrn)}
                                        />
                                        {incidentErrors[index] && incidentErrors[index].rrn && (
                                            <small className={styles.errorMsg}>{incidentErrors[index].rrn}</small>
                                        )}
                                    </div>
                                </div>

                                <div className={styles.formRow}>
                                    <div className={styles.formField}>
                                        <label htmlFor={`date-${index}`} className={styles.label}>
                                            Transaction Date
                                        </label>
                                        <Calendar id={`date-${index}`}
                                            name="date"
                                            placeholder="YYYY-MM-DD" className="calendar1"
                                            value={incident.date}
                                            onChange={(e) => handleChangeInc(index, e)}
                                            onFocus={() => handleFocusInc(index, 'date')} showIcon dateFormat="yy-mm-dd"
                                            aria-invalid={!!(incidentErrors[index] && incidentErrors[index].date)} />

                                        {/* <InputText
                                        id={`date-${index}`}
                                        name="date"
                                        placeholder="YY-MM-DD"
                                        value={incident.date}
                                        onChange={(e) => handleChangeInc(index, e)}
                                        onFocus={() => handleFocusInc(index, 'date')}
                                        className={styles.inputField}
                                        aria-invalid={!!(incidentErrors[index] && incidentErrors[index].date)}
                                    /> */}
                                        {incidentErrors[index] && incidentErrors[index].date && (
                                            <small className={styles.errorMsg}>{incidentErrors[index].date}</small>
                                        )}
                                    </div>
                                    <div className={styles.formField}>
                                        <label htmlFor={`time-${index}`} className={styles.label}>
                                            Transaction Time
                                        </label>
                                        <Calendar id={`time-${index}`}
                                            name="time" className="calendar1"
                                            placeholder="HH:MM:SS"
                                            value={incident.time}
                                            onChange={(e) => handleChangeInc(index, e)} 
                                            onFocus={() => handleFocusInc(index, 'time')} showIcon 
                                            aria-invalid={!!(incidentErrors[index] && incidentErrors[index].time)} timeOnly icon={() => <i className="pi pi-clock" />} showSeconds={true} />

                                        {/* <InputText
                                        id={`time-${index}`}
                                        name="time"
                                        placeholder="HH:MM:SS"
                                        value={incident.time}
                                        onChange={(e) => handleChangeInc(index, e)}
                                        onFocus={() => handleFocusInc(index, 'time')}
                                        className={styles.inputField}
                                        aria-invalid={!!(incidentErrors[index] && incidentErrors[index].time)}
                                    /> */}
                                        {incidentErrors[index] && incidentErrors[index].time && (
                                            <small className={styles.errorMsg}>{incidentErrors[index].time}</small>
                                        )}
                                    </div>
                                </div>

                                <div className={styles.formRow}>
                                    <div className={styles.formField}>
                                        <label htmlFor={`disamount-${index}`} className={styles.label}>
                                            Disputed Amount
                                        </label>
                                        <InputText
                                            id={`disamount-${index}`}
                                            name="disamount"
                                            placeholder="Enter disputed Amount"
                                            value={incident.disamount}
                                            onChange={(e) => handleChangeInc(index, e)}
                                            onFocus={() => handleFocusInc(index, 'disamount')}
                                            className={styles.inputField}
                                            aria-invalid={!!(incidentErrors[index] && incidentErrors[index].disamount)}
                                        />
                                        {incidentErrors[index] && incidentErrors[index].disamount && (
                                            <small className={styles.errorMsg}>{incidentErrors[index].disamount}</small>
                                        )}
                                    </div>
                                    <div className={styles.formField}>
                                        <label htmlFor={`layer-${index}`} className={styles.label}>
                                            Layer
                                        </label>
                                        <InputText
                                            id={`layer-${index}`}
                                            name="layer"
                                            placeholder="Enter layer"
                                            value={incident.layer}
                                            onChange={(e) => handleChangeInc(index, e)}
                                            onFocus={() => handleFocusInc(index, 'layer')}
                                            className={styles.inputField}
                                            aria-invalid={!!(incidentErrors[index] && incidentErrors[index].layer)}
                                        />
                                        {incidentErrors[index] && incidentErrors[index].layer && (
                                            <small className={styles.errorMsg}>{incidentErrors[index].layer}</small>
                                        )}
                                    </div>
                                </div>

                                {isCreditFraudSelected && (
                                    <>
                                        <div className={styles.formRow}>
                                            <div className={styles.formField}>
                                                <label htmlFor={`first6Digit-${index}`} className={styles.label}>
                                                    First 6 Digit
                                                </label>
                                                <InputText
                                                    id={`first6Digit-${index}`}
                                                    name="first6Digit"
                                                    placeholder="Enter first 6 digits"
                                                    value={incident.first6Digit}
                                                    onChange={(e) => handleChangeInc(index, e)}
                                                    onFocus={() => handleFocusInc(index, 'first6Digit')}
                                                    className={styles.inputField}
                                                    aria-invalid={!!(incidentErrors[index] && incidentErrors[index].first6Digit)}
                                                />
                                                {incidentErrors[index] && incidentErrors[index].first6Digit && (
                                                    <small className={styles.errorMsg}>{incidentErrors[index].first6Digit}</small>
                                                )}
                                            </div>
                                            <div className={styles.formField}>
                                                <label htmlFor={`last4Digit-${index}`} className={styles.label}>
                                                    Last 4 Digit
                                                </label>
                                                <InputText
                                                    id={`last4Digit-${index}`}
                                                    name="last4Digit"
                                                    placeholder="Enter last 4 digits"
                                                    value={incident.last4Digit}
                                                    onChange={(e) => handleChangeInc(index, e)}
                                                    onFocus={() => handleFocusInc(index, 'last4Digit')}
                                                    className={styles.inputField}
                                                    aria-invalid={!!(incidentErrors[index] && incidentErrors[index].last4Digit)}
                                                />
                                                {incidentErrors[index] && incidentErrors[index].last4Digit && (
                                                    <small className={styles.errorMsg}>{incidentErrors[index].last4Digit}</small>
                                                )}
                                            </div>
                                        </div>
                                        <div className={styles.formRow}>
                                            <div className={styles.formField}>
                                                <label htmlFor={`cardLength-${index}`} className={styles.label}>
                                                    Card Length
                                                </label>
                                                <InputText
                                                    id={`cardLength-${index}`}
                                                    name="cardLength"
                                                    placeholder="Enter card length"
                                                    value={incident.cardLength}
                                                    onChange={(e) => handleChangeInc(index, e)}
                                                    onFocus={() => handleFocusInc(index, 'cardLength')}
                                                    className={styles.inputField}
                                                    aria-invalid={!!(incidentErrors[index] && incidentErrors[index].cardLength)}
                                                />
                                                {incidentErrors[index] && incidentErrors[index].cardLength && (
                                                    <small className={styles.errorMsg}>{incidentErrors[index].cardLength}</small>
                                                )}
                                            </div>
                                        </div>
                                    </>
                                )}
                            </div>
                        ))}

                        <div className={styles.submitButtonContainer}>
                            <Button
                                label="Submit"
                                type="submit"
                                severity="primary"
                                className={`${styles.submitButton} ${styles.buttonSize}`}
                            />
                        </div>

                        <Dialog header="Incident Response" visible={displayDialog} style={{ width: '50vw' }} footer={dialogFooter} onHide={() => setDisplayDialog(false)}>
                            <div ref={captureRef} style={{ padding: '10px' }}>
                                Acknowledgement number : {incidentResponse.acknowledgement_no}<br />
                                Job ID : {incidentResponse.job_id}
                            </div>
                        </Dialog>
                    </form>
                </div>
            </div>
        </>
    );
};

export default RegisterComplaint;
